import { computed, ref, type ComputedRef, type Ref, type VNode } from 'vue'
import type { FloatingWindow, FloatingWindowConfig } from '../types'
import { clamp, getAABBSize, VEC2, Vector, type AABB } from 'math'
import { useElement } from '@/utils/dom/useElement'
import { Grid } from 'components/grid'
import CloseIcon from 'assets/img/svg/icons/close.svg?url'
import Icon from 'components/icon'
import { useElementBounding, useWindowSize } from '@vueuse/core'
import { useWindowEvents } from '@/utils/hooks/useWindowEvents'
import { useMousePosition } from '@/utils/hooks/useMousePosition'
import { useEffect } from '@/utils/reactive/useEffect'

export type UseWindow = {
  window: Ref<FloatingWindow>
  rendered: ComputedRef<VNode>
}

export type UseWindowProps = {
  window: FloatingWindowConfig;
  uid: string;
}

export const useWindow = (props: UseWindowProps): UseWindow => {
  const window = ref<FloatingWindow>({
    ...props.window,
    uid: props.uid,
    pos: VEC2(0, 0),
    dragPos: VEC2(0, 0),
    dragging: false
  })

  const browserWindow = useWindowSize();
  const mouse = useMousePosition();
  const windowEvents = useWindowEvents();
  const element = useElement()
  const bounding = useElementBounding(element.element as any);

  const getBox = (): AABB => {
    bounding.update()
    const pos = window.value.pos;
    const size = VEC2(bounding.width.value, bounding.height.value)
    return {
      min: pos,
      max: pos.add(size)
    }
  }

  const onMouseDown = (ev: MouseEvent) => {
    const box = getBox();
    const pos = VEC2(ev.clientX, ev.clientY).sub(box.min);
    window.value.dragPos = pos;
    window.value.dragging = true;
  }

  const onClose = () => {
    if (props.window.onClose) {
      props.window.onClose();
    }
  }

  windowEvents.addEventListener('mouseup', () => {
    window.value.dragging = false;
  });

  const setPos = (pos: Vector) => {
    const windowBounds: AABB = {
      min: VEC2(0, 0),
      max: VEC2(browserWindow.width.value, browserWindow.height.value)
    }

    const box = getBox();
    const size = getAABBSize(box);
    window.value.pos = pos;
    window.value.pos.x = clamp(window.value.pos.x, windowBounds.min.x, windowBounds.max.x - size.x);
    window.value.pos.y = clamp(window.value.pos.y, windowBounds.min.y, windowBounds.max.y - size.y);
  }

  useEffect(() => {
    if (!window.value.dragging) return;
    setPos(mouse.position.value.sub(window.value.dragPos));
  }, [() => window.value.dragging, () => mouse.position.value])

  const rendered = computed(() => {
    return (
      <div
        class="min-w-[300px] min-h-4 bg-white shadow-lg rounded-sm overflow-hidden virtual-window"
        style={{
          position: 'fixed',
          left: window.value.pos.x + 'px',
          top: window.value.pos.y + 'px',
          pointerEvents: 'all',
          userSelect: 'none',
          ...(window.value.resizable ? {
            resize: 'both'
          } : {}),
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '99svb',
          maxWidth: '99vw'
        }}
        {...element.props}
      >
        <Grid
          onMousedown={onMouseDown}
          class="h-12 bg-black text-white px-3"
          columns="max-content max-content"
          justifyContent="space-between"
          alignItems="center"
          style={{
            flex: 'none'
          }}
        >
          <Grid noDefaultLayout alignItems="center">
            <div class="text-white text-sm">{window.value.title}</div>
          </Grid>
          <Grid noDefaultLayout alignItems="center">
            <Icon
              class="cursor-pointer"
              src={CloseIcon}
              size={'1rem'}
              stroke="currentColor"
              onClick={onClose}
            />
          </Grid>
        </Grid>
        <div class="bg-white p-3 overflow-y-auto pretty-scrollbar" style={{
          flexGrow: '1',
          overflowY: 'auto'
        }}>
          {window.value.body ? <window.value.body window={window} /> : null}
        </div>
      </div>
    )
  })

  return { window, rendered }
}
